import { useEffect, useMemo, useState } from "react";
import useI18nContext from "../hooks/general/useI18nContext";
import Dialog from "./Dialog";
import { CSVLink } from "react-csv";
import { Button, CircularProgress, Stack } from "@mui/material";
import * as XLSX from "xlsx";
import { getAllActivities } from "../lib/API/queries/getActivities";
import getActivitiesCSV from "../lib/helpers/getActivitiesCSV";

export default function ActivitiesDownloadDialog({ open, onClose, query, users }) {
    const { t } = useI18nContext();
    const [csvData, setCsvData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        let isMounted = true;

        async function fetchData() {
            setIsLoading(true);
            try {
                // Fetch ALL activities matching the filters
                const allActivities = await getAllActivities(query);
                const data = getActivitiesCSV(allActivities, t, users);
                if (isMounted) setCsvData(data);
            } catch (e) {
                console.error("Failed to fetch activities", e);
                if (isMounted) setCsvData(null);
            } finally {
                if (isMounted) setIsLoading(false);
            }
        }

        if (open) {
            fetchData();
        }

        return () => {
            isMounted = false;
        };
    }, [open, query, t, users]);

    // Create first-name-only version for Excel export
    const firstNameOnlyData = useMemo(() => {
        if (!csvData || !Array.isArray(csvData) || csvData.length === 0) return null;

        const [headerRow, ...rows] = csvData;

        // Find the volunteer name column (שם מלגאי/ת)
        const nameColIndex = headerRow.indexOf(t?.username || "שם מלגאי/ת");
        if (nameColIndex === -1) {
            return csvData;
        }

        const newRows = rows.map((row) => {
            const newRow = [...row];
            const fullName = newRow[nameColIndex];

            if (typeof fullName === "string") {
                // Get only the first name (text before first space)
                const firstName = fullName.trim().split(/\s+/)[0];
                newRow[nameColIndex] = firstName;
            }

            return newRow;
        });

        return [headerRow, ...newRows];
    }, [csvData, t]);

    const handleExcelDownload = () => {
        if (!firstNameOnlyData) return;

        try {
            // Create Excel worksheet from AOA
            const worksheet = XLSX.utils.aoa_to_sheet(firstNameOnlyData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Activities");

            // Write workbook as ArrayBuffer
            const wbArray = XLSX.write(workbook, {
                bookType: "xlsx",
                type: "array",
            });

            // Create Excel Blob
            const blob = new Blob([wbArray], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });

            // Create download link and trigger download
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = "activities-list.xlsx";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);

            onClose();
        } catch (error) {
            console.error("Excel export failed:", error);
            alert(t?.serverError || "Export failed");
        }
    };

    return (
        <Dialog
            maxWidth="xs"
            fullWidth
            title={t?.download}
            open={open}
            onClose={onClose}
        >
            <Stack spacing={2}>
                {isLoading ? (
                    <Button fullWidth variant="contained" disabled>
                        <CircularProgress color="inherit" size={26} />
                    </Button>
                ) : (
                    <CSVLink
                        data={csvData || []}
                        filename="activities-list.csv"
                        style={{ textDecoration: "none" }}
                    >
                        <Button fullWidth variant="contained">
                            {t?.downloadFullName || "הורדה – שם מלא (CSV)"}
                        </Button>
                    </CSVLink>
                )}

                {isLoading || !firstNameOnlyData ? (
                    <Button fullWidth variant="outlined" disabled>
                        <CircularProgress size={26} />
                    </Button>
                ) : (
                    <Button fullWidth variant="outlined" onClick={handleExcelDownload}>
                        {t?.downloadFirstName || "הורדה – שם פרטי בלבד (Excel)"}
                    </Button>
                )}
            </Stack>
        </Dialog>
    );
}
